/**
 * User-coupon (direct assignment) query hooks using React Relay.
 *
 * Lists a user's `UserCoupon` grants filtered by `userOldId`, loading the
 * granted `coupon` plus audit/usage fields. This is the user-centric inverse
 * of `useUserCouponsByCoupon` (which lists by `couponOldId` and loads `user`),
 * powering the coupons section on the User Details page.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries to
 * regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useUserCouponsByUserQuery as UseUserCouponsByUserQuery,
  useUserCouponsByUserQuery$variables,
  UserCouponFilterInput,
} from './__generated__/useUserCouponsByUserQuery.graphql';
import type { UserCouponStatusFilter } from './useUserCouponsByCoupon';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const UserCouponsByUserQuery = graphql`
  query useUserCouponsByUserQuery(
    $first: Int
    $after: String
    $filter: UserCouponFilterInput
  ) {
    listUserCoupons(first: $first, after: $after, filter: $filter) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          assignedBy
          couponOldId
          userOldId
          userId
          created
          timesUsed
          removedOn
          coupon {
            id
            oldId
            name
            code
            discount
            discountType
            organizationId
          }
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { UserCouponFilterInput };

export interface UserCouponCouponGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  code?: string | null;
  discount?: number | null;
  discountType?: string | null;
  organizationId?: string | null;
}

export interface UserCouponByUserGraphQL {
  id: string;
  assignedBy?: string | null;
  couponOldId: string;
  userOldId: string;
  userId?: string | null;
  created?: string | null;
  timesUsed?: number | null;
  removedOn?: string | null;
  coupon?: UserCouponCouponGraphQL | null;
}

export type { UserCouponStatusFilter };

export interface UserCouponsByUserFilter {
  /** The user's legacy `oldId` (phone) to list grants for. */
  userOldId: string;
  /**
   * Which grants to return by revoke-status. Defaults to `ACTIVE`.
   *   - `ACTIVE`  -> non-revoked grants only
   *   - `REVOKED` -> revoked grants only
   *   - `ALL`     -> both
   */
  status?: UserCouponStatusFilter;
  /**
   * @deprecated Use `status` instead. Retained for backwards compatibility:
   * `activeOnly: false` is equivalent to `status: 'ALL'`.
   */
  activeOnly?: boolean;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * React Query-like hook (no Suspense) for listing a user's user-coupon
 * grants. Provides `data`, `isLoading`, `refetch`, etc.
 */
export function useUserCouponsByUser(
  filter: UserCouponsByUserFilter | undefined,
  options?: { limit?: number; enabled?: boolean }
) {
  const [data, setData] = useState<UserCouponByUserGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const enabled = options?.enabled !== false && Boolean(filter?.userOldId);

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled || !filter?.userOldId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useUserCouponsByUserQuery$variables = {
          first: limit,
          filter: buildRelayFilter(filter),
        };

        const result = await fetchQuery<UseUserCouponsByUserQuery>(
          relayEnvironment,
          UserCouponsByUserQuery,
          variables,
          { fetchPolicy: 'network-only' }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listUserCoupons?.edges?.map((edge) => edge?.node).filter(Boolean) ??
            [];
          setData(nodes as UserCouponByUserGraphQL[]);
          setCount(result.listUserCoupons?.count ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch user coupons'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

function buildRelayFilter(
  filter?: UserCouponsByUserFilter
): UserCouponFilterInput | undefined {
  if (!filter?.userOldId) return undefined;

  const relayFilter: UserCouponFilterInput = {
    userOldId: { eq: filter.userOldId },
  };

  // Resolve the effective status. `status` takes precedence; fall back to the
  // legacy `activeOnly` flag, defaulting to ACTIVE-only grants.
  const status: UserCouponStatusFilter =
    filter.status ?? (filter.activeOnly === false ? 'ALL' : 'ACTIVE');

  if (status === 'ACTIVE') {
    relayFilter.removedOn = { isNil: true };
  } else if (status === 'REVOKED') {
    relayFilter.removedOn = { isNil: false };
  }
  // status === 'ALL' -> leave `removedOn` unset (no filter).

  return relayFilter;
}
